import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { getDealRoomBrandedName } from '../../../../utils/locale';
import { get } from 'lodash';
import { getAvatarStyle, isNewStyleAvatar } from '../../../../lib/s3';
import { parseDate } from '../shared/dateUtils';
import ParticipantName from '../shared/ParticipantName';
import { 
  ShoppingCartIcon, 
  ChatBubbleLeftRightIcon, 
  BriefcaseIcon,
  StarIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';

/**
 * ConversationItem - Dashboard-style conversation item with succession theming
 * 
 * Features bigger, blockier design with succession marketplace colors
 */
const ConversationItem = ({ 
  conversation, 
  currentUser, 
  otherParticipant, 
  isSelected, 
  isOnline, 
  typingIndicator, 
  onClick
}) => {
  const intl = useIntl();
  if (!conversation || !otherParticipant) return null;

  const displayName = otherParticipant.allowed_name || otherParticipant.name || 'Unknown User';
  const hasUnread = conversation.unread_count > 0;
  const lastMessage = conversation.lastMessages?.[0] || conversation.last_message;
  const isBlocked = conversation.blocked_by_me || conversation.blocked_by_them;

  // Always show: "Oct 28, 9:48 AM" for consistency with Messages section
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = parseDate(timestamp);
      if (isNaN(date.getTime())) return 'Invalid date';
      const locale = (typeof window !== 'undefined' && window.locale) || 'en';
      const dateString = date.toLocaleDateString(locale, { month: 'short', day: 'numeric' });
      const timeString = date.toLocaleTimeString(locale, { hour: 'numeric', minute: '2-digit' });
      return `${dateString}, ${timeString}`;
    } catch (error) {
      console.warn('Error formatting conversation time:', timestamp, error);
      return '1d';
    }
  };

  // Strip HTML from message content
  const stripHtml = (html) => {
    if (!html) return '';
    // Remove HTML tags and decode common HTML entities
    const text = html
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/&nbsp;/g, ' ') // Replace non-breaking spaces
      .replace(/&amp;/g, '&') // Replace &amp; with &
      .replace(/&lt;/g, '<') // Replace &lt; with <
      .replace(/&gt;/g, '>') // Replace &gt; with >
      .replace(/&quot;/g, '"') // Replace &quot; with "
      .replace(/&#39;/g, "'") // Replace &#39; with '
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim(); // Remove leading/trailing whitespace
    return text;
  };

  // Format last message preview
  const formatLastMessagePreview = (message) => {
    if (!message) return intl.formatMessage({ id: 'succession.messaging.conversation_item.no_messages' });

    if (message.message_type === 'multiple_choice') {
      return intl.formatMessage({ id: 'succession.messaging.conversation_item.multiple_choice_preview' });
    }

    // Add sender name for clarity
    const senderName = message.sender?.id === currentUser?.id ? intl.formatMessage({ id: 'succession.messaging.conversation_item.you' }) : (message.sender?.allowed_name || message.sender?.name || 'Unknown');
    const cleanBody = stripHtml(message.body || '');
    const preview = cleanBody.length > 45 ? cleanBody.substring(0, 45) + '...' : cleanBody;
    
    return `${senderName}: ${preview}`;
  };

  // Get conversation type info - Succession themed with Heroicon components
  const getConversationTypeInfo = (type, company = null) => {
    // Check if this is a student conversation
    const isStudentConversation = otherParticipant?.is_student || conversation?.participant1?.is_student || conversation?.participant2?.is_student;
    const iconClass = "w-3.5 h-3.5 inline-block mr-1";

    const types = {
      deal: {
        label: getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.conversation_item.deal_room_label' }),
        icon: <BriefcaseIcon className={iconClass} />,
        color: 'text-succession-primary',
        bgColor: 'bg-succession-primary-light'
      },
      deal_interest: {
        label: getDealRoomBrandedName(company) || intl.formatMessage({ id: 'succession.messaging.conversation_item.interest_label' }),
        icon: <BriefcaseIcon className={iconClass} />,
        color: 'text-succession-primary',
        bgColor: 'bg-succession-primary-light'
      },
      partial_sale_listing: {
        label: intl.formatMessage({ id: 'succession.messaging.conversation_item.partial_sale_label' }),
        icon: <ShoppingCartIcon className={iconClass} />,
        color: 'text-succession-warning',
        bgColor: 'bg-succession-warning-light'
      },
      support_request: {
        label: intl.formatMessage({ id: 'succession.messaging.conversation_item.support_label' }),
        icon: <LifebuoyIcon className={iconClass} />,
        color: 'text-succession-error',
        bgColor: 'bg-succession-error-light'
      },
      paddy: {
        label: intl.formatMessage({ id: 'succession.messaging.conversation_item.paddy_label' }),
        icon: <StarIcon className={iconClass} />,
        color: 'text-succession-secondary',
        bgColor: 'bg-succession-secondary-light'
      },
      message: {
        label: intl.formatMessage({ id: 'succession.messaging.conversation_item.message_label' }),
        icon: <ChatBubbleLeftRightIcon className={iconClass} />,
        color: 'text-succession-text-muted',
        bgColor: 'bg-succession-bg-secondary'
      }
    };
    return types[type] || {
      label: intl.formatMessage({ id: 'succession.messaging.conversation_item.message_label' }),
      icon: <ChatBubbleLeftRightIcon className={iconClass} />,
      color: 'text-succession-text-muted',
      bgColor: 'bg-succession-bg-secondary'
    };
  };

  // Generate clean avatar colors using succession colors
  const getAvatarColorClass = (name) => {
    const colors = [
      'bg-succession-primary', 'bg-succession-primary', 'bg-succession-error', 'bg-succession-warning', 'bg-succession-secondary',
      'bg-succession-success', 'bg-succession-error', 'bg-succession-secondary', 'bg-succession-warning', 'bg-succession-primary'
    ];
    
    const charCode = name.charCodeAt(0) || 0;
    return colors[charCode % colors.length];
  };

  return (
    <div 
      className={`px-4 py-3 cursor-pointer transition-colors border-l-2 ${isBlocked ? 'opacity-60' : ''}`}
      style={{
        backgroundColor: isSelected ? '#E8F0FE' : 'transparent',
        borderLeftColor: isSelected ? 'var(--succession-primary)' : 'transparent'
      }}
      onMouseEnter={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = '#F3F4F6';
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Avatar - Demo style: 40px, simple border */}
        <div className="relative w-10 h-10 rounded-full flex-shrink-0 overflow-hidden bg-[var(--succession-bg-tertiary)]">
          {get(otherParticipant, 'profile_picture') ? (
            <img
              src={getAvatarStyle(get(otherParticipant, 'profile_picture', '')).url}
              alt={displayName}
              className="w-full h-full object-cover"
              style={isNewStyleAvatar(get(otherParticipant, 'profile_picture', '')) ? { objectFit: 'contain', objectPosition: 'bottom center', transform: 'scale(0.85)', transformOrigin: 'bottom center' } : {}}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          
          {/* Fallback initial avatar */}
          <div className={`${(get(otherParticipant, 'student_avatar_url') || get(otherParticipant, 'profile_picture')) ? 'hidden' : 'flex'} w-full h-full bg-[#4A90E2] items-center justify-center text-white font-medium text-sm`}>
            {displayName.charAt(0).toUpperCase()}
          </div>

          {/* Online Status - small green dot */}
          {isOnline && (
            <div className="absolute bottom-0 right-0 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-[var(--succession-bg-primary)]" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Header Row - Name with unread badge, and Time */}
          <div className="flex items-center justify-between mb-0.5">
            <div className="flex items-center gap-2 min-w-0">
              <ParticipantName
                participant={otherParticipant}
                stopPropagation
                className={`text-sm truncate min-w-0 ${
                  hasUnread ? 'font-semibold text-[var(--succession-text-primary)]' : 'font-medium text-[var(--succession-text-primary)]'
                }`}
              >
                {displayName}
              </ParticipantName>

              {/* Unread Badge - Demo style: circular, primary color, next to name */}
              {hasUnread && (
                <div 
                  className="w-5 h-5 text-white text-xs font-medium rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: 'var(--succession-primary)' }}
                >
                  {conversation.unread_count}
                </div>
              )}
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0 ml-2">
              {lastMessage && (
                <span className="text-xs text-[var(--succession-text-muted)]">
                  {formatLastMessageTime(lastMessage.createdAt || lastMessage.created_at) || '1d'}
                </span>
              )}
            </div>
          </div>

          {/* Firm Name */}
          {otherParticipant?.advisor_firm_name && !otherParticipant?.is_student && (
            <div className="text-xs text-[var(--succession-text-muted)] truncate mb-0.5">
              {otherParticipant.advisor_firm_name}
            </div>
          )}

          {/* Message Preview */}
          <div className={`text-sm truncate ${
            hasUnread ? 'text-[var(--succession-text-primary)]' : 'text-[var(--succession-text-secondary)]'
          }`}>
            {typingIndicator ? (
              <span className="italic" style={{ color: 'var(--succession-primary)' }}>{intl.formatMessage({ id: 'succession.messaging.conversation_item.is_typing' }, { name: displayName })}</span>
            ) : (
              formatLastMessagePreview(lastMessage)
            )}
          </div>

          {/* Bottom Row - Type badge with icon */}
          {(() => {
            const convType = conversation.conversationType || conversation.conversation_type;
            const typeInfo = getConversationTypeInfo(convType, conversation.company);
            return (
              <div className="mt-1.5 flex items-center gap-2">
                <span className="text-xs text-[var(--succession-text-muted)] flex items-center">
                  {typeInfo.icon}
                  {typeInfo.label}
                </span>
                {otherParticipant?.is_student && (
                  <span className="text-xs font-semibold leading-none px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                    {intl.formatMessage({ id: 'succession.messaging.conversation_item.recruit_badge' })}
                  </span>
                )}
              </div>
            );
          })()}

          {/* Blocked indicator */}
          {isBlocked && (
            <div className="text-xs text-red-500 mt-1">
              🚫 {conversation.blocked_by_me ? intl.formatMessage({ id: 'succession.messaging.conversation_item.blocked_by_me' }) : intl.formatMessage({ id: 'succession.messaging.conversation_item.blocked_by_them' })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * TypingDots - Succession themed typing indicator
 */
const TypingDots = () => {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="w-1 h-1 bg-succession-primary rounded-full animate-pulse"
          style={{
            animation: 'typing-dot-small 1.4s infinite ease-in-out',
            animationDelay: `${i * 0.2}s`
          }}
        />
      ))}
    </div>
  );
};

ConversationItem.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    unread_count: PropTypes.number,
    blocked_by_me: PropTypes.bool,
    blocked_by_them: PropTypes.bool,
    conversation_type: PropTypes.string,
    conversationType: PropTypes.string,
    participant1: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      allowed_name: PropTypes.string,
      is_student: PropTypes.bool
    }),
    participant2: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      allowed_name: PropTypes.string,
      is_student: PropTypes.bool
    }),
    lastMessages: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      body: PropTypes.string,
      createdAt: PropTypes.string,
      messageType: PropTypes.string,
      sender: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        allowed_name: PropTypes.string
      })
    })),
    // Legacy support for snake_case
    last_message: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      body: PropTypes.string,
      created_at: PropTypes.string,
      message_type: PropTypes.string,
      sender: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
        allowed_name: PropTypes.string
      })
    })
  }).isRequired,
  currentUser: PropTypes.object,
  otherParticipant: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    allowed_name: PropTypes.string,
    is_admin: PropTypes.bool,
    is_student: PropTypes.bool
  }),
  isSelected: PropTypes.bool,
  isOnline: PropTypes.bool,
  typingIndicator: PropTypes.shape({
    user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    user_name: PropTypes.string,
    timestamp: PropTypes.number
  }),
  onClick: PropTypes.func
};

export default ConversationItem; 